import type { FileData, SortOption } from "../types";
import {
  addGuestBookmark,
  isGuestBookmarkSession,
  isGuestBookmarked,
  removeGuestBookmark,
} from "./guestBookmarkSession";

export const RECENT_VIEWED_EVENT = "recent:viewed";
export const RECENT_EDITED_EVENT = "recent:edited";

const RECENT_STORAGE_PREFIX = "recent:";
const EDITED_STORAGE_PREFIX = "edited:";

const LAST_VIEWED_FALLBACK_KEYS: Array<keyof FileData> = [
  "updated_at",
  "updated",
  "lastEdited",
  "last_modified",
  "modified",
];

const DATE_CREATED_KEYS: Array<keyof FileData> = [
  "created_at",
  "createdAt",
  "timestamp",
];

export const getFileKey = (file: FileData | null | undefined): string =>
  file?.gcs_path ||
  file?.full_path ||
  file?.name ||
  file?.filename ||
  file?.id ||
  "";

/** gs://bucket/uid/... 과 uid/... 가 동일 항목으로 매칭되도록 키 정규화 */
export const canonicalFileKeyForStorage = (
  path: string | null | undefined
): string => {
  if (!path) return "";
  const p = String(path).trim();
  if (p.startsWith("gs://")) {
    const parts = p.split("/");
    if (parts.length > 3) {
      return parts.slice(3).join("/");
    }
  }
  return p;
};

export const getCanonicalFileKey = (file: FileData | null | undefined): string =>
  canonicalFileKeyForStorage(
    file?.gcs_path ||
      file?.full_path ||
      file?.name ||
      file?.filename ||
      file?.id ||
      ""
  );

export const getRecentStorageKey = (fileKey: string): string =>
  `${RECENT_STORAGE_PREFIX}${fileKey}`;

/** localStorage `bookmark:${fileKey}` — 값은 ISO 시각(누른 순서) 또는 레거시 `"1"` */
export const BOOKMARK_STORAGE_PREFIX = "bookmark:";

export const getBookmarkStorageKey = (fileKey: string): string =>
  `${BOOKMARK_STORAGE_PREFIX}${fileKey}`;

/** 레거시 `"1"` 또는 ISO 문자열이면 북마크로 간주 */
export const isBookmarkedInStorage = (fileKey: string): boolean => {
  if (!fileKey || typeof window === "undefined") return false;
  // Guest가 누른 북마크는 남기지 않으므로 localStorage를 보지 않는다.
  if (isGuestBookmarkSession()) return isGuestBookmarked(fileKey);
  try {
    const v = localStorage.getItem(getBookmarkStorageKey(fileKey));
    if (v == null || v === "") return false;
    if (v === "1") return true;
    const t = Date.parse(v);
    return !Number.isNaN(t);
  } catch {
    return false;
  }
};

export const setBookmarkPressedNow = (fileKey: string): void => {
  if (!fileKey || typeof window === "undefined") return;
  if (isGuestBookmarkSession()) {
    addGuestBookmark(fileKey);
    return;
  }
  localStorage.setItem(getBookmarkStorageKey(fileKey), new Date().toISOString());
};

export const removeBookmarkFromStorage = (fileKey: string): void => {
  if (!fileKey || typeof window === "undefined") return;
  if (isGuestBookmarkSession()) {
    removeGuestBookmark(fileKey);
    return;
  }
  localStorage.removeItem(getBookmarkStorageKey(fileKey));
};

/** @returns 켜진 뒤 true, 꺼진 뒤 false */
export const toggleBookmarkInStorage = (fileKey: string): boolean => {
  if (!fileKey || typeof window === "undefined") return false;
  if (isBookmarkedInStorage(fileKey)) {
    removeBookmarkFromStorage(fileKey);
    return false;
  }
  setBookmarkPressedNow(fileKey);
  return true;
};

export const parseTimestamp = (
  value: string | number | null | undefined
): number => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getTimeByKeys = (
  file: FileData,
  keys: Array<keyof FileData>
): number => {
  for (const key of keys) {
    const value = file[key];
    if (value != null) {
      const parsed = parseTimestamp(value as string | number);
      if (parsed > 0) return parsed;
    }
  }
  return 0;
};

export const readStoredLastViewed = (
  fileOrKey: FileData | string | null | undefined
): string | null => {
  const fileKey =
    typeof fileOrKey === "string" ? fileOrKey : getFileKey(fileOrKey);
  if (!fileKey || typeof window === "undefined") return null;
  try {
    return localStorage.getItem(getRecentStorageKey(fileKey));
  } catch {
    return null;
  }
};

export const getEffectiveLastViewedTime = (file: FileData): number => {
  const apiLv = parseTimestamp(
    (file as { last_viewed_at?: string | number | null })?.last_viewed_at
  );
  if (apiLv > 0) return apiLv;
  const stored = readStoredLastViewed(file);
  const storedTime = parseTimestamp(stored);
  if (storedTime > 0) return storedTime;
  return parseTimestamp(file.lastViewed);
};

export const getServerLastModifiedTime = (file: FileData): number =>
  getTimeByKeys(file, LAST_VIEWED_FALLBACK_KEYS);

export const getDateCreatedTime = (file: FileData): number =>
  getTimeByKeys(file, DATE_CREATED_KEYS);

export const readStoredLastEdited = (
  fileOrKey: FileData | string | null | undefined
): string | null => {
  const canonical =
    typeof fileOrKey === "string"
      ? canonicalFileKeyForStorage(fileOrKey)
      : getCanonicalFileKey(fileOrKey);
  if (!canonical || typeof window === "undefined") return null;
  try {
    return localStorage.getItem(`${EDITED_STORAGE_PREFIX}${canonical}`);
  } catch {
    return null;
  }
};

/** 서버 수정 시각과 클라이언트에 기록한 저장 완료 시각 중 더 최근 값(ms) */
export const getEffectiveLastModifiedTime = (file: FileData): number => {
  const server = getServerLastModifiedTime(file);
  const stored = parseTimestamp(readStoredLastEdited(file));
  return Math.max(server, stored);
};

/** 홈 최근 목록에서 사용하는 통합 최신 활동 시각(ms) */
export const getEffectiveRecencyTime = (file: FileData): number => {
  const edited = getEffectiveLastModifiedTime(file);
  const viewed = getEffectiveLastViewedTime(file);
  if (edited > 0 || viewed > 0) {
    return Math.max(edited, viewed);
  }
  return getDateCreatedTime(file);
};

/**
 * 백엔드 `sort`와 동일한 클라이언트 정렬 비교.
 * - lastViewed: 최근 활동 시각 최신순(내림차순)
 * - dateCreated: 생성일 오래된 순(오름차순)
 */
export const compareFilesBySortMode = (
  a: FileData,
  b: FileData,
  currentSort: SortOption
): number => {
  if (currentSort === "dateCreated") {
    const createdDiff = getDateCreatedTime(a) - getDateCreatedTime(b);
    if (createdDiff !== 0) return createdDiff;

    const updatedDiff = getServerLastModifiedTime(a) - getServerLastModifiedTime(b);
    if (updatedDiff !== 0) return updatedDiff;

    return getEffectiveRecencyTime(a) - getEffectiveRecencyTime(b);
  }

  const recencyDiff = getEffectiveRecencyTime(b) - getEffectiveRecencyTime(a);
  if (recencyDiff !== 0) return recencyDiff;

  const updatedDiff = getServerLastModifiedTime(b) - getServerLastModifiedTime(a);
  if (updatedDiff !== 0) return updatedDiff;

  return getDateCreatedTime(b) - getDateCreatedTime(a);
};

export const sortFilesByOption = (
  files: FileData[],
  currentSort: SortOption
): FileData[] => {
  return [...files].sort((a, b) => compareFilesBySortMode(a, b, currentSort));
};

/** 서버 `is_bookmarked` 우선, 없으면 localStorage(레거시). Guest는 세션 메모리만 본다. */
export const isFileBookmarkedForDisplay = (file: FileData): boolean => {
  if (isGuestBookmarkSession()) return isBookmarkedInStorage(getFileKey(file));
  if (typeof file.is_bookmarked === "boolean") return file.is_bookmarked;
  return isBookmarkedInStorage(getFileKey(file));
};

/**
 * 북마크한 항목을 목록 맨 앞으로 고정한다.
 *
 * 각 구간 안에서는 들어온 순서를 그대로 유지하는 안정 분할이라, 사용자 파일 정렬과
 * "샘플은 사용자 파일 뒤" 규칙을 깨지 않고 북마크만 앞으로 끌어올린다.
 */
export const pinBookmarkedFirst = (
  files: FileData[],
  isBookmarked: (file: FileData) => boolean,
): FileData[] => {
  const pinned: FileData[] = [];
  const rest: FileData[] = [];

  for (const file of files) {
    (isBookmarked(file) ? pinned : rest).push(file);
  }

  return [...pinned, ...rest];
};

/**
 * 북마크 항목을 **위에 모은 뒤**, 북마크 구간·비북마크 구간 모두 `currentSort`와 같은 규칙으로 정렬.
 * - lastViewed: 각 구간 안에서 최근 활동 **최신순**
 * - dateCreated: 각 구간 안에서 생성일 **오래된 순**
 */
export const orderFilesWithBookmarksFirst = (
  files: FileData[],
  currentSort: SortOption,
  isBookmarkedKey: (key: string) => boolean
): FileData[] => {
  const items = [...files];
  const marked = items.filter((f) => isBookmarkedKey(getFileKey(f)));
  const unmarked = items.filter((f) => !isBookmarkedKey(getFileKey(f)));

  marked.sort((a, b) => compareFilesBySortMode(a, b, currentSort));
  unmarked.sort((a, b) => compareFilesBySortMode(a, b, currentSort));

  return [...marked, ...unmarked];
};

export const markFileAsRecentlyViewed = (
  file: FileData | null | undefined,
  viewedAt: string = new Date().toISOString()
): string | null => {
  const fileKey = getFileKey(file);
  if (!fileKey || typeof window === "undefined") return null;

  try {
    localStorage.setItem(getRecentStorageKey(fileKey), viewedAt);
    window.dispatchEvent(
      new CustomEvent(RECENT_VIEWED_EVENT, {
        detail: {
          fileKey,
          viewedAt,
        },
      })
    );
    return viewedAt;
  } catch {
    return null;
  }
};

/** GCS 반영 완료 시점(에디터 저장 SSE 완료 등) — 백엔드 updated_at 갱신 전에도 홈 그리드에 반영 */
export const markFileAsRecentlyEdited = (
  pathOrFile: string | FileData | null | undefined,
  editedAt: string = new Date().toISOString()
): string | null => {
  const canonical =
    typeof pathOrFile === "string"
      ? canonicalFileKeyForStorage(pathOrFile)
      : getCanonicalFileKey(pathOrFile);
  if (!canonical || typeof window === "undefined") return null;

  try {
    localStorage.setItem(`${EDITED_STORAGE_PREFIX}${canonical}`, editedAt);
    window.dispatchEvent(
      new CustomEvent(RECENT_EDITED_EVENT, {
        detail: {
          fileKey: canonical,
          editedAt,
        },
      })
    );
    return editedAt;
  } catch {
    return null;
  }
};
